import type { DerivedStats, GameState, MasterDie } from '../../../core/types';
import { dieCardInnerHtml } from '../../format';
import { overlayRoot } from '../../columnScale';
import { equipRefusal } from '../../equipApi';
import { icon } from '../../icons';
import { registerEscape } from '../../keyboard';
import { toast } from '../../toast';
import type { ScreenCtx } from '../../types';
import { sortInventory } from './dice';
import { buildDiceViewPanel, buildHiddenNotice } from './diceViewPanel';
import { DEFAULT_DICE_VIEW, filterDice, hasActiveFilter, presentMaterials, presentSides, readDiceView, sortDice, type DiceView } from '../../diceView';
import { dieScore } from '../../../core/dice';
import { decorateDieCard, favoritesFirst } from '../../dieQuick';

/**
 * Fenêtre « Équiper l'emplacement N » (A88-8) : ouverte par un toucher sur un
 * emplacement vide de l'onglet Dés. Elle liste les dés Maîtres de l'inventaire
 * qui ne sont pas équipés, par niveau décroissant puis rareté puis puissance
 * (tri par défaut des dés, v0.53.0, A103), chacun avec « Équiper ici ».
 */

/** Dés de la réserve (non équipés), avant tout filtre : niveau, rareté, puis puissance (A103). */
function spareDice(state: Readonly<GameState>, stats: DerivedStats): MasterDie[] {
  const equipped = new Set(state.equipped.filter((id): id is string => typeof id === 'string'));
  return sortInventory(state, stats, 'level').filter((d) => !equipped.has(d.id));
}

/**
 * Dés Maîtres de la réserve (non équipés), filtrés et triés par la vue « Tri et
 * filtres » (A119). Clé `level` (défaut) : niveau, rareté, puis puissance
 * (A103) ; les autres clés partent de cet ordre, qui départage leurs égalités.
 */
export function equipCandidates(
  state: Readonly<GameState>,
  stats: DerivedStats,
  view: DiceView = DEFAULT_DICE_VIEW,
): MasterDie[] {
  const base = filterDice(spareDice(state, stats), view);
  // A122-8 : les favoris d'abord (leur ordre relatif suit la vue).
  return favoritesFirst(view.sort === 'level' ? base : sortDice(base, view, (d) => dieScore(d, stats)));
}

export const NO_SPARE_DICE_TEXT = 'Aucun dé Maître en réserve : fabriquez-en à la Boutique.';

export function openEquipSlotModal(ctx: ScreenCtx, slotIndex: number, refresh: () => void): void {
  const { game } = ctx;

  const overlay = document.createElement('div');
  overlay.className = 'f-scrim';
  overlay.setAttribute('data-role', 'equip-slot-modal');
  overlay.innerHTML = `
    <div class="f-modal equip-modal" role="dialog" aria-modal="true" aria-labelledby="equip-slot-title">
      <button type="button" class="f-ibtn equip-modal-close" data-action="close-equip" aria-label="Fermer">${icon('close')}</button>
      <h2 class="f-modal-title" id="equip-slot-title">Équiper l'emplacement ${slotIndex + 1}</h2>
      <div class="equip-modal-body" data-role="equip-slot-body"></div>
    </div>
  `;
  const body = overlay.querySelector<HTMLElement>('[data-role="equip-slot-body"]') as HTMLElement;

  let unregisterEscape = (): void => {};
  const close = (): void => {
    unregisterEscape();
    overlay.remove();
  };

  /** (Re)dessine le corps : le panneau de vue, puis la liste ; un changement de vue le rappelle. */
  const renderBody = (): void => {
    body.replaceChildren();
    const stats = game.getDerivedStats();
    const view = readDiceView();
    const spare = spareDice(game.state, stats);
    const candidates = equipCandidates(game.state, stats, view);
    if (spare.length === 0) {
      const none = document.createElement('p');
      none.className = 'f-modal-text';
      none.setAttribute('data-role', 'equip-slot-empty');
      none.textContent = NO_SPARE_DICE_TEXT;
      body.appendChild(none);
      return;
    }
    body.appendChild(
      buildDiceViewPanel({ onChange: renderBody, materials: presentMaterials(game.state.inventory), sides: presentSides(game.state.inventory) }),
    );
    const notice = buildHiddenNotice(hasActiveFilter(view) ? spare.length - candidates.length : 0, renderBody);
    if (notice) body.appendChild(notice);
    if (candidates.length === 0) {
      const none = document.createElement('p');
      none.className = 'f-modal-text';
      none.setAttribute('data-role', 'equip-slot-filtered-empty');
      none.textContent = 'Aucun dé ne correspond aux filtres.';
      body.appendChild(none);
      return;
    }
    const list = document.createElement('ul');
    list.className = 'inventory-list equip-modal-list';
    list.setAttribute('data-role', 'equip-slot-list');
    for (const die of candidates) {
      const li = document.createElement('li');
      li.className = 'die-card die-card--inventory f-cut';
      li.dataset.rarity = die.rarity;
      li.dataset.dieId = die.id;
      li.innerHTML = dieCardInnerHtml(die, '', game);
      decorateDieCard(li, die, game, renderBody);
      const actions = document.createElement('div');
      actions.className = 'die-card-actions';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'f-btn f-btn--sm';
      btn.textContent = 'Équiper ici';
      btn.setAttribute('data-action', 'equip-here');
      btn.addEventListener('click', () => {
        // Même refus que le bouton « Équiper » des cartes (A72-14).
        const refusal = equipRefusal(game, die.id, slotIndex);
        if (refusal) {
          toast(refusal, 2600, 'lock');
          return;
        }
        game.equipDie(die.id, slotIndex);
        close();
        refresh();
      });
      actions.appendChild(btn);
      li.appendChild(actions);
      list.appendChild(li);
    }
    body.appendChild(list);
  };
  renderBody();

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('[data-action="close-equip"]')?.addEventListener('click', close);
  overlayRoot().appendChild(overlay);
  unregisterEscape = registerEscape(overlay, close);
  overlay.querySelector<HTMLButtonElement>('[data-action="close-equip"]')?.focus();
}
